/**
 * Fact Finder Form Types
 * These types define the structure of encrypted data in the fact finder
 */

// ============================================
// APPLICANT PERSONAL DETAILS
// ============================================

export type PreferredContactMethod = "email" | "mobile" | "home_phone" | "work_phone";

export interface ApplicantPersonalDetails {
  // Basic mode fields (always collected)
  title: string;
  firstName: string;
  middleNames?: string;
  surname: string;
  dateOfBirth: string;
  gender: string;
  // Contact details (basic mode)
  preferredContactMethod?: PreferredContactMethod;
  homePhone?: string;
  mobile: string;
  workPhone?: string;
  email: string;
  countryOfResidence?: string;
  // Employment status (basic mode)
  isSelfEmployed?: boolean;
  // Advanced mode fields (only collected when advancedMode is true)
  isFirstHomeBuyer?: boolean;
  isPermanentResident?: boolean;
  maidenName?: string;
  driversLicenceNo?: string;
  driversLicenceState?: string;
  driversLicenceExpiry?: string;
  maritalStatus?: string;
  numberOfDependants?: number;
  dependantAges?: string;
  // Nearest relative/friend (advanced mode)
  nearestRelativeName?: string;
  nearestRelativeAddress?: string;
  nearestRelativeRelationship?: string;
  nearestRelativePhone?: string;
}

export interface AddressDetails {
  residencyType: "own_home" | "mortgaged" | "renting" | "boarding" | "other";
  rentPerWeek?: number;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  dateMovedIn: string;
}

export interface PreviousAddress {
  address: string;
  dateMovedIn: string;
  dateMovedOut: string;
}

export interface ApplicantData {
  personal: ApplicantPersonalDetails;
  currentAddress: AddressDetails;
  previousAddresses: PreviousAddress[];
  sameAsApplicant1?: boolean; // For applicant 2
}

// ============================================
// EMPLOYMENT DETAILS
// ============================================

export type EmploymentType =
  | "full_time"
  | "part_time"
  | "casual"
  | "contract"
  | "self_employed"
  | "home_duties";

export type EmployerType = "public_sector" | "private_sector";

export interface EmploymentDetails {
  employmentType: EmploymentType;
  employerName: string;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  startDate: string;
  endDate?: string; // For previous employment
  contactPerson?: string;
  contactPhone?: string;
  employerType: EmployerType;
  occupation: string;
  isOnProbation?: boolean;
  probationRemaining?: string;
}

export interface ApplicantEmployment {
  current: EmploymentDetails;
  previous: EmploymentDetails[];
}

export interface EmploymentData {
  applicant1: ApplicantEmployment;
  applicant2?: ApplicantEmployment;
}

// ============================================
// ASSETS
// ============================================

export interface PropertyAsset {
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  propertyType: "house" | "townhouse" | "unit" | "land" | "other";
  currentValue: number;
  datePurchased: string;
  rentalPerWeek?: number;
  owner: "joint" | "applicant1" | "applicant2" | "trust_other";
  loanBalance?: number;
  repaymentAmount?: number;
  lender?: string;
}

export interface SavingsAccount {
  accountType: string;
  bankInstitution: string;
  numberOfAccounts: number;
  amount: number;
  heldBy: "applicant1" | "applicant2" | "joint";
}

export interface ShareHolding {
  heldWith: string;
  numberOfShares: number;
  value: number;
  heldBy: "applicant1" | "applicant2" | "joint";
}

export interface Superannuation {
  company: string;
  value: number;
  heldBy: "applicant1" | "applicant2";
}

export interface Vehicle {
  make: string;
  model: string;
  year: number;
  value: number;
  heldBy: "applicant1" | "applicant2" | "joint";
}

export interface OtherAsset {
  description: string;
  value: number;
}

export interface AssetsData {
  properties: PropertyAsset[];
  savings: SavingsAccount[];
  shares: ShareHolding[];
  superannuation: Superannuation[];
  vehicles: Vehicle[];
  otherAssets: OtherAsset[];
  homeContentsInsurance?: {
    company: string;
    insuredValue: number;
  };
  wantsInsuranceQuote: boolean;
}

// ============================================
// LIABILITIES
// ============================================

export interface CurrentLoan {
  loanType: string;
  lender: string;
  amountOwing: number;
  repaymentAmount: number;
  repaymentFrequency: "weekly" | "fortnightly" | "monthly";
  heldBy: "applicant1" | "applicant2" | "joint";
}

export interface CreditCard {
  cardType: string;
  lender: string;
  limit: number;
  amountOwing: number;
  heldBy: "applicant1" | "applicant2" | "joint";
}

export interface LiabilitiesData {
  loans: CurrentLoan[];
  creditCards: CreditCard[];
}

// ============================================
// INCOME
// ============================================

export interface IncomeData {
  applicant1Salary?: number;
  applicant2Salary?: number;
  businessProfit?: number;
  investmentIncome?: number;
  permanentPensions?: number;
  otherIncome?: number;
  otherIncomeDescription?: string;
  directDebit?: {
    preferredAccount: "new" | "existing";
    accountName?: string;
    bsb?: string;
    accountNumber?: string;
  };
}

// ============================================
// EXPENSES
// ============================================

export interface ExpensesData {
  groceries: number;
  personal: number;
  propertyOwnerOccupied: number;
  propertyInvestment: number;
  education: number;
  childCare: number;
  health: number;
  transport: number;
  insurance: number;
  entertainment: number;
  communication: number;
  childSupport: number;
  other: number;
  otherDescription?: string;
  totalMonthly: number;
}

// ============================================
// REQUIREMENTS & OBJECTIVES
// ============================================

export interface RequirementsData {
  primaryReason: string;
  desiredFeatures: {
    variableRate: boolean;
    fixedRate: boolean;
    lineOfCredit: boolean;
    principalAndInterest: boolean;
    extraRepaymentsAllowed: boolean;
    redraw: boolean;
    offsetAccount: boolean;
    packagedWithCreditCard: boolean;
    notSure: boolean;
  };
  seekingInterestOnly: boolean;
  interestOnlyReason?: string;
  expectedContractLength: "short" | "medium" | "long" | "not_sure";
  hasDefaultsOrJudgements: boolean;
  allCommitmentsUpToDate: boolean;
  concernAboutRisingRates: "not_concerned" | "a_little" | "very_concerned" | "not_sure";
  expectsFinancialChanges: boolean;
  financialChangesDescription?: string;
  wantsFinancialAdviserIntro: boolean;
}

// ============================================
// COMPLETE FACT FINDER
// ============================================

export interface FactFinderFormData {
  applicant1: ApplicantData;
  applicant2?: ApplicantData;
  employment: EmploymentData;
  assets: AssetsData;
  liabilities: LiabilitiesData;
  income: IncomeData;
  expenses: ExpensesData;
  requirements: RequirementsData;
  privacyConsent: {
    consented: boolean;
    consentedAt?: string;
    applicant1Signature?: string;
    applicant2Signature?: string;
  };
}

// Section names for progress tracking
export type FactFinderSection =
  | "applicants"
  | "employment"
  | "assets"
  | "liabilities"
  | "income"
  | "expenses"
  | "requirements"
  | "next-steps"
  | "consent";

export const FACT_FINDER_SECTIONS: FactFinderSection[] = [
  "applicants",
  "employment",
  "assets",
  "liabilities",
  "income",
  "expenses",
  "requirements",
  "next-steps",
  "consent",
];

export const SECTION_LABELS: Record<FactFinderSection, string> = {
  applicants: "Applicants",
  employment: "Employment",
  assets: "Assets",
  liabilities: "Liabilities",
  income: "Income",
  expenses: "Expenses",
  requirements: "Requirements",
  "next-steps": "Next Steps",
  consent: "Privacy & Consent",
};
